// SEO component, imperatively updates document head for reliable SPA SEO
import { useEffect } from "react";

const SITE_URL = "https://www.cityworthloans.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/img/og-default.jpg`;

// IDs let us re-use the same tag rather than appending duplicates
const META_IDS = {
  description: "meta-description",
  canonical: "link-canonical",
  robots: "meta-robots",
  ogType: "og-type",
  ogTitle: "og-title",
  ogDescription: "og-description",
  ogUrl: "og-url",
  ogImage: "og-image",
  twCard: "tw-card",
  twTitle: "tw-title",
  twDescription: "tw-description",
  twImage: "tw-image",
  jsonLd: "json-ld-page",
};

const setMeta = (id, attrs) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement(attrs.tag);
    el.id = id;
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "tag") return;
    if (v === null || v === undefined) {
      el.removeAttribute(k);
    } else {
      el.setAttribute(k, v);
    }
  });
};

const removeMeta = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

const removeStaticDuplicates = () => {
  // Remove any pre-existing duplicate tags from index.html that we now manage
  // (only run once, these tags are static and won't come back)
  const selectors = [
    'meta[name="description"]:not([id])',
    'link[rel="canonical"]:not([id])',
    'meta[property="og:type"]:not([id])',
    'meta[property="og:title"]:not([id])',
    'meta[property="og:description"]:not([id])',
    'meta[property="og:url"]:not([id])',
    'meta[property="og:image"]:not([id])',
    'meta[name="twitter:card"]:not([id])',
    'meta[name="twitter:title"]:not([id])',
    'meta[name="twitter:description"]:not([id])',
    'meta[name="twitter:image"]:not([id])',
    'meta[name="robots"]:not([id])',
  ];
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => el.remove());
  });
};

let _cleanedDuplicates = false;

export default function SEO({
  title,
  description,
  path = "/",
  ogImage = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd = null,
  noindex = false,
}) {
  useEffect(() => {
    if (!_cleanedDuplicates) {
      removeStaticDuplicates();
      _cleanedDuplicates = true;
    }

    const fullUrl = `${SITE_URL}${path}`;
    const fullTitle = title.includes("CityWorth") ? title : `${title} | CityWorth`;
    const absOgImage = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;

    document.title = fullTitle;

    setMeta(META_IDS.description, { tag: "meta", name: "description", content: description });
    setMeta(META_IDS.canonical, { tag: "link", rel: "canonical", href: fullUrl });

    if (noindex) {
      setMeta(META_IDS.robots, { tag: "meta", name: "robots", content: "noindex,nofollow" });
    } else {
      setMeta(META_IDS.robots, {
        tag: "meta",
        name: "robots",
        content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      });
    }

    setMeta(META_IDS.ogType, { tag: "meta", property: "og:type", content: type });
    setMeta(META_IDS.ogTitle, { tag: "meta", property: "og:title", content: fullTitle });
    setMeta(META_IDS.ogDescription, { tag: "meta", property: "og:description", content: description });
    setMeta(META_IDS.ogUrl, { tag: "meta", property: "og:url", content: fullUrl });
    setMeta(META_IDS.ogImage, { tag: "meta", property: "og:image", content: absOgImage });

    setMeta(META_IDS.twCard, { tag: "meta", name: "twitter:card", content: "summary_large_image" });
    setMeta(META_IDS.twTitle, { tag: "meta", name: "twitter:title", content: fullTitle });
    setMeta(META_IDS.twDescription, { tag: "meta", name: "twitter:description", content: description });
    setMeta(META_IDS.twImage, { tag: "meta", name: "twitter:image", content: absOgImage });

    if (jsonLd) {
      let el = document.getElementById(META_IDS.jsonLd);
      if (!el) {
        el = document.createElement("script");
        el.id = META_IDS.jsonLd;
        el.type = "application/ld+json";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(jsonLd);
    } else {
      removeMeta(META_IDS.jsonLd);
    }
  }, [title, description, path, ogImage, type, jsonLd, noindex]);

  return null;
}
